import { useEffect, useState } from 'react';
import { Row, Col, Button, Card, Space, DatePicker, InputNumber, message } from 'antd';

import { formatDate, formatToDollar, isSupportManager } from '../../../../helpers/genericHelper';
import Data from '../../../CustomText/data';
import API from '../../../../services/API';

export default function BloomAccountStatus({ productId, reloadData }) {

  const [bloomAccount, setBloomAccount] = useState(null);
  const [closureEffectiveDate, setClosureEffectiveDate] = useState(null);
  const [showClosureForm, setShowClosureForm] = useState(false);
  const [chargeOffEffectiveDate, setChargeOffEffectiveDate] = useState(null);
  const [chargeOffAmount, setChargeOffAmount] = useState(null);
  const [showChargeOffForm, setShowChargeOffForm] = useState(false);

  const resetClosureForm = () => {
    setShowClosureForm(false);
    setClosureEffectiveDate(null);
  };

  const resetChargeOffForm = () => {
    setShowChargeOffForm(false);
    setChargeOffEffectiveDate(null);
    setChargeOffAmount(null);
  };

  useEffect(() => {
    getBloomAccount();
  }, [productId]);

  const getBloomAccount = async () => {
    const bloomAccountData = await API.getFurnishingBloomAccount({ productId });
    setBloomAccount(bloomAccountData);
  }

  const postBloomAccountEvent = async (event, successMessage) => {
    try {
      const response = await API.postFurnishingBloomAccountEvent({
        accountId: bloomAccount.id.value,
        ...event
      });
      if (response.success) {
        message.success(successMessage);
        getBloomAccount();
      } else {
        message.error(response.message);
      }
      reloadData?.();
    } catch (error) {
      message.error('Failed to update bloom account.');
      console.error(error);
    }
  }

  const addClosure = async () => {
    await postBloomAccountEvent({
      eventType: 'CLOSURE',
      effectiveDate: closureEffectiveDate ? closureEffectiveDate.format('YYYY-MM-DD') : null
    }, 'Closure added.');
    resetClosureForm();
  };

  const reverseClosure = () => postBloomAccountEvent({ eventType: 'CLOSURE', reversed: true, effectiveDate: bloomAccount?.accountPosition?.effective_date }, 'Closure reversed.');

  const addChargeOff = async () => {
    await postBloomAccountEvent({
      eventType: 'CHARGE_OFF',
      effectiveDate: chargeOffEffectiveDate ? chargeOffEffectiveDate.format('YYYY-MM-DD') : null,
      amount: chargeOffAmount != null ? String(chargeOffAmount) : null
    }, 'Charge off added.');
    resetChargeOffForm();
  };

  const reverseChargeOff = () => postBloomAccountEvent({
    eventType: 'CHARGE_OFF',
    reversed: true,
    effectiveDate: bloomAccount?.chargeOff?.effective_date,
    amount: bloomAccount?.chargeOff?.amount
  }, 'Charge off reversed.');

  return (
    <Data title="Furnishing Status" value={<Row>
      <Col span="1"></Col>
      <Col span="20">
        <Space direction="horizontal" style={{ width: '100%' }}>
          { /** Closure */}
          <Card
            size="small"
            style={{
              width: 300,
            }}
          >
            { bloomAccount?.accountPosition?.effective_date && <Data isTag tagColor="error" title="Date" value={formatDate(bloomAccount?.accountPosition?.effective_date)} /> }
            { bloomAccount?.accountPosition?.created_at && <Data isTag tagColor="error" title="Created" value={formatDate(bloomAccount?.accountPosition?.created_at)} /> }
            { bloomAccount?.accountPosition ? null : <Data isTag tagColor="success" value="Not Closed" /> }
            
            { isSupportManager() && (bloomAccount?.accountPosition
              ? <Button type="link" onClick={() => reverseClosure()}>Reverse Closure</Button>
              : <Button type="link" onClick={() => setShowClosureForm(true)}>Add Closure</Button>)
            }

            { showClosureForm && <Space direction="vertical">
                <DatePicker
                  placeholder="Effective date"
                  value={closureEffectiveDate}
                  onChange={(date) => setClosureEffectiveDate(date)}
                />
                <Space>
                  <Button type="primary" onClick={() => addClosure()}>Submit</Button>
                  <Button onClick={resetClosureForm}>Cancel</Button>
                </Space>
              </Space>
            }

          </Card>
          { /** Charge Off */}
          <Card
            size="small"
            style={{
              width: 300,
            }}
          >
            { bloomAccount?.chargeOff?.effective_date && <Data isTag tagColor="error" title="Date" value={formatDate(bloomAccount?.chargeOff?.effective_date)} /> }
            { bloomAccount?.chargeOff?.created_at && <Data isTag tagColor="error" title="Created" value={formatDate(bloomAccount?.chargeOff?.created_at)} /> }
            { bloomAccount?.chargeOff?.amount && <Data isTag tagColor="error" title="Amount" value={formatToDollar(bloomAccount?.chargeOff?.amount)} /> }
            { bloomAccount?.chargeOff ? null : <Data isTag tagColor="success" value="Not Charged Off" /> }

            { isSupportManager() && (bloomAccount?.chargeOff
              ? <Button type="link" onClick={() => reverseChargeOff()}>Reverse Charge Off</Button>
              : <Button type="link" onClick={() => setShowChargeOffForm(true)}>Add Charge Off</Button>)
            }

            { showChargeOffForm && <Space direction="vertical">
                <DatePicker
                  placeholder="Effective date"
                  value={chargeOffEffectiveDate}
                  onChange={(date) => setChargeOffEffectiveDate(date)}
                />
                <InputNumber
                  placeholder="Amount"
                  value={chargeOffAmount}
                  onChange={(value) => setChargeOffAmount(value)}
                />
                <Space>
                  <Button type="primary" onClick={() => addChargeOff()}>Submit</Button>
                  <Button onClick={resetChargeOffForm}>Cancel</Button>
                </Space>
              </Space>
            }
          </Card>
          { /** Bankruptcy */}
          { <Card size="small" style={{
            width: 300,
          }}>
            { bloomAccount?.bankruptcy?.effective_date && <Data isTag tagColor="error" title="Date" value={formatDate(bloomAccount?.bankruptcy?.effective_date)} /> }
            { bloomAccount?.bankruptcy?.created_at && <Data isTag tagColor="error" title="Created" value={formatDate(bloomAccount?.bankruptcy?.created_at)} /> }
            { bloomAccount?.bankruptcy?.chapter && <Data isTag tagColor="error" title="Chapter" value={bloomAccount?.bankruptcy?.chapter} /> }
            { bloomAccount?.bankruptcy?.status && <Data isTag tagColor="error" title="Status" value={bloomAccount?.bankruptcy?.status} /> }
            { bloomAccount?.bankruptcy ? null : <Data isTag tagColor="success" value="No Bankruptcy" /> }
          </Card> }
        </Space>
      </Col>
    </Row>} />
  )
}
